import styled from "styled-components/native";
import OptionHeader from "../OptionHeader";
import DropDown from "../DropDown";
import { JewelryBuilderDropDownProps } from "./types";
import { useEffect, useRef, useState } from "react";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import ResetButtonComponent from "../ResetButton";
import { isKeyPresent } from "@scenes/configurator/JewelryConfigurator/utils";
import JewelryBuilderErrorMessage from "../JewelryBuilderErrorMessage";
import React from "react";
import { OptionStyle } from "@scenes/configurator/JewelryConfigurator/types";
import NativeDropDown from "./NativeDropDown";
import { DefaultTheme } from "styled-components/native";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import InputDrawer from "@components/molecules/InputDrawer/InputDrawer";
import { FlatList } from "react-native";
import { Button } from "@components/atoms";
import RadioButton from "@components/atoms/RadioButton";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { css } from "styled-components/native";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";

const JewelryBuilderDropDown = ({
  productType,
  configObject,
  fromReviewPage,
  isOpen,
  onToggle,
  fromOrderCart,
  fromRequest,
  showHeader = true,
  enterSecondFlow = false,
  onSelect,
}: JewelryBuilderDropDownProps) => {
  const {
    updateJewelryConfigInfo,
    jcSelectedOption,
    addToCartPressed,
    fetchValidationMessage,
    validateRequiredOptions,
  } = useJewelryConfiguratorState();
  const { theme, t } = useScreenOptionsHooks();

  if (configObject?.option_value && configObject?.option_value.length > 0) {
    configObject.option_value = configObject.option_value.filter(
      (item) => item.enabled
    );
  }

  const [selectedItem, setSelectedItem] = useState(
    configObject?.selected_item ??
      jcSelectedOption.current.option_value[configObject.option_type]
  );
  const [dropdownRevertKey, setDropdownRevertKey] = useState(0);

  const initialCenterSizeRef = useRef<string>(
    (() => {
      const fromContext =
        jcSelectedOption.current.option_value[configObject.option_type];
      if (fromContext) return fromContext;
      const preSelected = configObject.option_value?.find(
        (opt: any) => opt.value === configObject.selected_item
      );
      return (
        preSelected?.slug ||
        preSelected?.value ||
        configObject.selected_item ||
        ""
      );
    })()
  );

  const handleItemSelect = (item: any, closeModal?: () => void) => {
    const newValue = item.slug || item.value;
    const isCenterSize = configObject?.option_type === "center_size";

    if (enterSecondFlow && isCenterSize) {
      closeModal?.();
      const previousSelectedItem = selectedItem;
      const commitFn = () => {
        setSelectedItem(item.label ?? item.value);
        updateJewelryConfigInfo(configObject?.option_type, [
          configObject,
          newValue,
        ]);
        if (
          isKeyPresent(
            configObject.option_type,
            jcSelectedOption.current.option_error
          )
        ) {
          validateRequiredOptions();
        }
      };
      const revertFn = () => {
        setSelectedItem(previousSelectedItem);
        setDropdownRevertKey((prev) => prev + 1);
      };
      onSelect?.(initialCenterSizeRef.current, newValue, commitFn, revertFn);
      return;
    }

    closeModal?.();
    setSelectedItem(item.label ?? item.value);
    updateJewelryConfigInfo(configObject?.option_type, [
      configObject,
      newValue,
    ]);
    if (
      isKeyPresent(
        configObject.option_type,
        jcSelectedOption.current.option_error
      )
    ) {
      validateRequiredOptions();
    }
    if (isCenterSize) {
      onSelect?.(initialCenterSizeRef.current, newValue);
    }
  };

  useEffect(() => {}, [addToCartPressed]);

  useEffect(() => {
    setSelectedItem(
      configObject?.selected_item
        ? configObject?.selected_item
        : jcSelectedOption.current.option_value[configObject.option_type]
    );
  }, [
    configObject,
    jcSelectedOption.current.option_value[configObject.option_type],
  ]);

  const DropDownContent = ({ closeModal }: { closeModal: () => void }) => {
    return (
      <>
        <FlatList
          nestedScrollEnabled
          data={configObject.option_value}
          keyExtractor={(item, index) => `${item.value}-${index}`}
          style={{ padding: 12, paddingHorizontal: 16 }}
          renderItem={({ item }) => {
            return (
              <>
                <RadioButton
                  label={item.label}
                  checked={selectedItem === item.value}
                  labelColor={
                    selectedItem === item.value
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                  onPress={() => handleItemSelect(item, closeModal)}
                />
              </>
            );
          }}
        />
        <ButtonContainer>
          <ContainerOverlay>
            <Button
              type={ButtonType.primary}
              category={ButtonCategory.default}
              title={t("global.text.done")}
              onPress={closeModal}
            />
          </ContainerOverlay>
        </ButtonContainer>
      </>
    );
  };

  return (
    <ContainerView>
      {showHeader && (
        <OptionHeader
          title={configObject.option_name}
          value={selectedItem}
          productType={productType}
          helperText={configObject?.option_helper_text}
          fromReviewPage={fromReviewPage}
          isOpen={isOpen}
          onToggle={onToggle}
          fromOrderCart={fromOrderCart}
          fromRequest={fromRequest}
          disableToggleButton={
            (configObject?.disable_modify_variation ?? false) ||
            configObject?.option_type === "center_size"
          }
        />
      )}
      {(isOpen || !fromReviewPage) && (
        <>
          {configObject?.option_style === OptionStyle.dropDownSelect &&
          theme.isLargeScreen ? (
            <NativeDropDown
              defaultValue={selectedItem}
              options={configObject.option_value}
              productType={productType}
              isSubOptions={false}
              showError={isKeyPresent(
                configObject.option_type,
                jcSelectedOption.current.option_error
              )}
              onSelect={(_value, selected) => handleItemSelect(selected)}
            />
          ) : theme.isMobile ? (
            <InputDrawer
              children={<DropDownContent closeModal={() => {}} />}
              itemText={t(selectedItem)}
              headerTitle={t(configObject.option_name)}
              iconName={VDBIcons.FILLED_CIRCLE_DOWN}
              iconColor={theme["main-01"]}
              borderRadius={SpaceType.Extra}
            />
          ) : (
            <DropDown
              key={dropdownRevertKey}
              defaultValue={selectedItem}
              options={configObject.option_value}
              productType={productType}
              isSubOptions={false}
              showError={isKeyPresent(
                configObject.option_type,
                jcSelectedOption.current.option_error
              )}
              onSelect={(_value, selected) => handleItemSelect(selected)}
            />
          )}
          {showHeader && (
            <BottomContainer
              showError={isKeyPresent(
                configObject.option_type,
                jcSelectedOption.current.option_error
              )}
            >
              {isKeyPresent(
                configObject.option_type,
                jcSelectedOption.current.option_error
              ) && (
                <JewelryBuilderErrorMessage
                  message={fetchValidationMessage(
                    configObject.option_name,
                    false
                  )}
                />
              )}
              {configObject?.reset_button_visible && (
                <ResetButtonComponent productType={productType} />
              )}
            </BottomContainer>
          )}
        </>
      )}
    </ContainerView>
  );
};

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 12px 16px;
  width: 100%;
  gap: 12px;
`;

const BottomContainer = styled.View<{
  showError: boolean;
}>`
  flex-direction: row;
  justify-content: ${({ showError }: { showError: boolean }) =>
    showError ? "space-between" : "flex-end"};
  margin-top: -4px;
`;

const ButtonContainer = styled.View`
  padding-top: ${SpaceType.Big}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
  padding-horizontal: ${SpaceType.Double}px;
  align-items: center;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

export default JewelryBuilderDropDown;
